// The two sign-in steps (email, then the six-digit code), shared by the
// sign-in page and the "save your progress" sheet. `signInSteps` renders them;
// `bindSignInSteps` makes them work and returns a cleanup function.
import { html, raw } from '../lib/html.js';
import { icon } from '../lib/icons.js';
import { local, setLocal } from '../lib/store.js';
import { requestCode, verifyCode } from '../lib/sync.js';
import { normalizeEmail, cleanCode, CODE_LENGTH } from '../../shared/auth.js';
import { openSheet } from './sheet.js';
import { toast } from './toast.js';

function busy(btn, on) {
  btn.disabled = on;
  btn.classList.toggle('is-busy', on);
  btn.setAttribute('aria-busy', on ? 'true' : 'false');
}

// copy: { eyebrow, title, lede, submit } for the email step. In `compact`
// mode (inside a sheet, which has its own title) the steps show no eyebrow or
// heading of their own.
export function signInSteps({ id = 'si', copy = {}, compact = false, headingLevel = 1, autofocus = true } = {}) {
  const H = raw(`h${headingLevel}`);
  const hClass = headingLevel === 1 ? 'h1' : 'h2';
  const slot = (i) => html`<span class="otp__slot" data-slot="${i}"></span>`;
  return html`
    <section class="signin__step" data-step="email"${compact ? '' : raw(` aria-labelledby="${id}-email-title"`)}>
      ${compact ? '' : html`<p class="eyebrow">${copy.eyebrow || 'Sign in or create an account'}</p>
      <${H} class="${hClass} signin__title" id="${id}-email-title">${copy.title || 'Keep your place on every device'}</${H}>`}
      <p class="signin__lede">${copy.lede || "Enter your email and we'll send you a 6-digit code. There's no password to remember."}</p>
      <form class="signin__form" data-email-form novalidate>
        <label class="field__label" for="${id}-email">Email address</label>
        <input class="input signin__input" id="${id}-email" name="email" type="email" autocomplete="email" inputmode="email" autocapitalize="none" autocorrect="off" spellcheck="false" enterkeyhint="send" placeholder="you@example.com" value="${local().lastEmail || ''}" aria-describedby="${id}-email-error"${autofocus ? raw(' data-autofocus') : ''} required>
        <p class="form-error" id="${id}-email-error" role="alert" data-email-error hidden></p>
        <button type="submit" class="btn btn--primary btn--block signin__submit" data-send>${copy.submit || 'Email me a code'} ${icon('arrow-right', { size: 20 })}</button>
      </form>
    </section>
    <section class="signin__step" data-step="code"${compact ? '' : raw(` aria-labelledby="${id}-code-title"`)} hidden>
      ${compact ? '' : html`<p class="eyebrow signin__eyebrow">${icon('mail-check', { size: 16 })}<span>Check your email</span></p>
      <${H} class="${hClass} signin__title" id="${id}-code-title">Enter your code</${H}>`}
      <p class="signin__lede" id="${id}-code-lede">We sent a 6-digit code to <strong class="signin__email" data-email></strong>. It expires in 10 minutes.</p>
      <form class="signin__form" data-code-form novalidate>
        <label class="field__label" for="${id}-code">6-digit code</label>
        <div class="otp" data-otp>
          <input class="otp__input" id="${id}-code" name="code" type="text" inputmode="numeric" autocomplete="one-time-code" enterkeyhint="done" autocorrect="off" spellcheck="false" aria-describedby="${id}-code-lede ${id}-code-error" data-code required>
          <div class="otp__slots" aria-hidden="true">${[0, 1, 2].map(slot)}<span class="otp__gap"></span>${[3, 4, 5].map(slot)}</div>
        </div>
        <p class="form-error" id="${id}-code-error" role="alert" data-code-error hidden></p>
        <button type="submit" class="btn btn--primary btn--block signin__submit" data-verify>${copy.verify || 'Sign in'}</button>
      </form>
      <div class="signin__again">
        <button type="button" class="btn btn--ghost btn--small" data-resend disabled>Resend code</button>
        <span class="signin__dot" aria-hidden="true"></span>
        <button type="button" class="btn btn--ghost btn--small" data-change>Use a different email</button>
      </div>
      <p class="signin__help">Can't find it? Look in spam or promotions for an email from <span class="signin__from" data-from>bhagavadgita@vibecod3.app</span>.</p>
    </section>`;
}

// onStep(step) runs when the visible step changes; onSignedIn(result) after a
// successful sign-in (the account and this device are merged by then).
export function bindSignInSteps(root, { onStep = () => {}, onSignedIn = () => {} } = {}) {
  const $ = (s) => root.querySelector(s);
  const emailForm = $('[data-email-form]');
  const emailInput = emailForm.querySelector('input');
  const emailError = $('[data-email-error]');
  const sendBtn = $('[data-send]');
  const codeForm = $('[data-code-form]');
  const codeInput = $('[data-code]');
  const codeError = $('[data-code-error]');
  const verifyBtn = $('[data-verify]');
  const otp = $('[data-otp]');
  const slots = [...root.querySelectorAll('.otp__slot')];
  const resendBtn = $('[data-resend]');
  let email = '';
  let resendAt = 0;
  let timer = null;
  let sending = false;
  let verifying = false;

  const say = (el, message) => {
    el.textContent = message || '';
    el.hidden = !message;
  };

  function show(step) {
    for (const s of root.querySelectorAll('section[data-step]')) {
      const on = s.dataset.step === step;
      s.hidden = !on;
      s.classList.toggle('is-entering', on);
    }
    onStep(step);
    if (step === 'code') {
      codeInput.focus();
    } else {
      emailInput.focus();
      emailInput.select();
    }
  }

  // The six boxes mirror one real input, so autofill, paste, screen readers
  // and password managers all see an ordinary text field.
  function paint() {
    const v = codeInput.value;
    slots.forEach((s, i) => {
      s.textContent = v[i] || '';
      s.classList.toggle('is-filled', i < v.length);
      s.classList.toggle('is-active', i === Math.min(v.length, CODE_LENGTH - 1));
    });
    otp.classList.toggle('is-focused', document.activeElement === codeInput);
  }

  const toEnd = () => {
    const n = codeInput.value.length;
    try {
      codeInput.setSelectionRange(n, n);
    } catch {
      // Not every input type supports selection; the boxes still work.
    }
  };

  function updateResend() {
    const left = Math.ceil((resendAt - Date.now()) / 1000);
    if (left > 0) {
      resendBtn.disabled = true;
      resendBtn.textContent = `Resend code in ${left}s`;
    } else {
      resendBtn.disabled = false;
      resendBtn.textContent = 'Resend code';
      clearInterval(timer);
      timer = null;
    }
  }

  function countdown(seconds) {
    resendAt = Date.now() + seconds * 1000;
    clearInterval(timer);
    timer = setInterval(updateResend, 1000);
    updateResend();
  }

  function fail(message, { clear = false } = {}) {
    say(codeError, message);
    otp.classList.remove('is-error');
    void otp.offsetWidth; // restart the shake
    otp.classList.add('is-error');
    if (clear) codeInput.value = '';
    codeInput.focus();
    paint();
  }

  async function send({ resend = false } = {}) {
    if (sending) return;
    sending = true;
    const btn = resend ? resendBtn : sendBtn;
    busy(btn, true);
    say(emailError, '');
    try {
      const res = await requestCode(email);
      setLocal({ lastEmail: email });
      $('[data-from]').textContent = res.from;
      countdown(res.resendIn);
      if (resend) {
        codeInput.value = '';
        say(codeError, '');
        otp.classList.remove('is-error');
        toast('A new code is on its way');
        codeInput.focus();
        paint();
      } else {
        show('code');
      }
    } catch (e) {
      if (e.code === 'cooldown') {
        // A code went to this address moments ago, and it still works.
        countdown((e.data && e.data.retryAfter) || 30);
        if (!resend) show('code');
      } else if (resend) {
        fail(e.message);
      } else {
        say(emailError, e.code === 'offline' ? "You're offline. Connect to the internet to sign in." : e.message);
        emailInput.focus();
      }
    } finally {
      sending = false;
      busy(btn, false);
      if (resend || timer) updateResend();
    }
  }

  async function submitCode() {
    if (verifying) return;
    const code = cleanCode(codeInput.value);
    if (code.length !== CODE_LENGTH) {
      fail('Enter all 6 digits from the email.');
      return;
    }
    verifying = true;
    busy(verifyBtn, true);
    codeInput.readOnly = true;
    try {
      const res = await verifyCode(email, code);
      setLocal({ welcomed: true });
      clearInterval(timer);
      onSignedIn(res);
    } catch (e) {
      verifying = false;
      busy(verifyBtn, false);
      codeInput.readOnly = false;
      // A dead code cannot be retried, so a new one may be requested now.
      if (['too_many', 'expired', 'no_code'].includes(e.code)) countdown(0);
      fail(e.code === 'offline' ? "You're offline. Connect to the internet and try again." : e.message, { clear: e.code !== 'offline' });
    }
  }

  emailForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const value = normalizeEmail(emailInput.value);
    if (!value) {
      emailInput.setAttribute('aria-invalid', 'true');
      say(emailError, 'Please enter a valid email address, like name@example.com.');
      emailInput.focus();
      return;
    }
    emailInput.removeAttribute('aria-invalid');
    email = value;
    $('[data-email]').textContent = value;
    send();
  });
  emailInput.addEventListener('input', () => {
    emailInput.removeAttribute('aria-invalid');
    say(emailError, '');
  });

  codeForm.addEventListener('submit', (e) => {
    e.preventDefault();
    submitCode();
  });
  codeInput.addEventListener('input', () => {
    const v = cleanCode(codeInput.value);
    if (v !== codeInput.value) codeInput.value = v;
    otp.classList.remove('is-error');
    say(codeError, '');
    paint();
    if (v.length === CODE_LENGTH) submitCode();
  });
  codeInput.addEventListener('focus', () => {
    toEnd();
    paint();
  });
  codeInput.addEventListener('blur', paint);
  codeInput.addEventListener('click', toEnd);
  codeInput.addEventListener('keyup', toEnd);

  resendBtn.addEventListener('click', () => send({ resend: true }));
  $('[data-change]').addEventListener('click', () => {
    clearInterval(timer);
    timer = null;
    codeInput.value = '';
    say(codeError, '');
    otp.classList.remove('is-error');
    paint();
    show('email');
  });

  paint();
  return () => clearInterval(timer);
}

// "Save your progress": sign in without leaving the page. Resolves true once
// signed in, false if the sheet is closed first.
export function openSignInSheet({ title = 'Save your progress', lede } = {}) {
  return new Promise((resolve) => {
    let done = false;
    let cleanup = () => {};
    const { el, close } = openSheet({
      title,
      className: 'sheet--signin',
      body: html`<div class="signin-sheet">${signInSteps({
        id: 'sis',
        compact: true,
        autofocus: false,
        copy: {
          lede: lede || "We'll email you a 6-digit code. Signing in keeps your progress safe and ready on any phone or computer.",
        },
      })}</div>`,
      onClose: () => {
        cleanup();
        if (!done) resolve(false);
      },
    });
    cleanup = bindSignInSteps(el, {
      onSignedIn: (res) => {
        done = true;
        toast(res.created ? 'Saved. Your progress now syncs to your account.' : 'Signed in. Your progress is synced.');
        resolve(true);
        close();
      },
    });
    setTimeout(() => el.querySelector('[data-email-form] input').focus(), 60);
  });
}
